import React, { useEffect, useState } from 'react';
import { Modal, Card, Row, Col, Badge, Spinner, Image } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { getUserData } from '../../store/slices/invitesSlice';
import { FiUser, FiMail, FiPhone, FiClock, FiRadio } from 'react-icons/fi';

const SectionTitle = ({ title, icon }) => (
  <h5 className="mt-4 mb-3 d-flex align-items-center text-primary">
    {icon && <span className="me-2">{icon}</span>}
    {title}
  </h5>
);

const RenderValue = ({ label, value, icon }) => (
    <Row className="mb-2 align-items-center">
      <Col xs={5} className="d-flex align-items-center text-secondary">
        {icon && <span className="me-2">{icon}</span>}
        <span className="font-weight-medium">{label}</span>
      </Col>
      <Col xs={7} className="d-flex align-items-center">
        {Array.isArray(value) ? (
          <div className="d-flex flex-wrap gap-1">
            {value.map((item, idx) => (
              <Badge key={idx} bg="info" className="py-2">
                {item}
              </Badge>
            ))}
          </div>
        ) : React.isValidElement(value) ? (
          value
        ) : (
          <span className={value ? 'text-dark' : 'text-muted'}>
            {value ? String(value) : 'N/A'}
          </span>
        )}
      </Col>
    </Row>
  );

const UserLookupPopup = ({ show, onClose, id }) => {
  const dispatch = useDispatch();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (id && show) {
        try {
          setLoading(true);
          setError(null);
          const data = await dispatch(getUserData(id)).unwrap();
          setUserData(data);
        } catch (err) {
          setError('Failed to fetch user data. Please try again.');
          console.error('Error fetching user data:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => setUserData(null);
  }, [id, show, dispatch]);

  const renderUserProfile = () => (
    <div className="d-flex align-items-center mb-4">
      {userData?.attributes?.stationLogo ? (
        <Image 
          src={userData.attributes.stationLogo} 
          roundedCircle 
          width={80} 
          height={80} 
          className="object-fit-cover border border-3 border-primary"
          alt="Station Logo"
        />
      ) : (
        <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" 
          style={{ width: 80, height: 80 }}>
          <FiUser size={32} className="text-muted" />
        </div>
      )}
      <div className="ms-3">
        <h4 className="mb-1">
          {userData?.attributes?.firstName} {userData?.attributes?.lastName}
        </h4>
        <p className="text-muted mb-0">{userData?.attributes?.stationName}</p>
        <small className="text-muted">{userData?.id}</small>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <SectionTitle title="Contact Information" icon={<FiUser />} />
        <RenderValue label="StationSchool" value={userData?.attributes?.stationSchool} icon={<FiMail />} />
        <RenderValue label="Admin Owner Name" value={userData?.attributes?.firstName+' '+userData?.attributes?.lastName} icon={<FiPhone />} />
        <RenderValue label="Admin Owner Email" value={userData?.id} icon={<FiMail />} />
      </Card.Body>
    </Card>
  );

  const renderStationInfo = () => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <SectionTitle title="Station Details" icon={<FiRadio />} />
        <RenderValue label="Station Name" value={userData?.attributes?.stationSchool} />
        <RenderValue label="Call Letter/City,State" value={userData?.attributes?.stationName} />
        <RenderValue label="Station Website" value={
          userData?.attributes?.stationWebsite ? (
            <a href={userData.attributes.stationWebsite} target="_blank" rel="noopener noreferrer">
              {userData.attributes.stationWebsite}
            </a>
          ) : 'N/A'
        } />
        <RenderValue label="Stream URL" value={
          userData?.attributes?.stream ? (
            <a href={userData.attributes.stream} target="_blank" rel="noopener noreferrer">
              {userData.attributes.stream}
            </a>
          ) : 'N/A'
        } />
      </Card.Body>
    </Card>
  );

  const renderAccountInfo = () => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <SectionTitle title="Account Information" icon={<FiClock />} />
        <RenderValue label="Account Type" value={userData?.type} />
        <RenderValue label="Roles" value={userData?.attributes?.roles} />
        <RenderValue label="Station Group" value={userData?.attributes?.stationGroup} />
      </Card.Body>
    </Card>
  );

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="border-bottom-0">
        <Modal.Title className="fw-bold">User Details</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Loading user profile...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
            </div>
          ) : userData ? (
            <>
              {renderUserProfile()}
              {renderContactInfo()}
              {renderStationInfo()}
              {renderAccountInfo()}
            </>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <FiUser size={48} />
              </div>
              <h5 className="text-muted">No user data available</h5>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default UserLookupPopup;